import { useMemo, useRef } from 'react';
import { DocumentSubtitleSegmentV1 } from '@utils/types/editors/subtitles/subtitle.ts';
import { debounce } from 'lodash';
import { calculateCPS } from '@utils/audio.ts';
import { useSharedCaption } from '@utils/providers/shared-caption-context.tsx';
import toast from 'react-hot-toast';
import TimestampInput from '@components/atoms/ui-kit/timestamp-input.tsx';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import cn from 'classnames';
import { useThemeContext } from '@utils/providers/theme-context.tsx';

type Counters = {
  lineLengths: number[];
  lineLimit: number;
  blockChars: number;
  blockLimit: number;
};

type Props = {
  segment: DocumentSubtitleSegmentV1;
  filteredItems: DocumentSubtitleSegmentV1[];
  counters?: Counters;
};

export const SharedStartStopTimeInputs = ({
  segment,
  filteredItems,
  counters
}: Props) => {
  const { t } = useTranslation();
  const { changeSegmentTime } = useSharedCaption();
  const { theme } = useThemeContext();

  // Theme-aware colors
  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const borderColor = pick('rgb(229 231 235)', 'rgb(55 65 81)'); // gray-200 / gray-700
  const counterBgColor = pick('rgb(239 246 255)', 'rgb(30 58 138)'); // blue-50 / blue-900
  const counterTextColor = pick('rgb(75 85 99)', 'rgb(156 163 175)'); // gray-600 / gray-400
  const idBgColor = pick('rgb(239 246 255)', 'rgb(30 58 138)'); // blue-50 / blue-900
  const idTextColor = pick('rgb(29 78 216)', 'rgb(147 197 253)'); // blue-700 / blue-300
  const idRingColor = pick(
    'rgba(29, 78, 216, 0.1)',
    'rgba(147, 197, 253, 0.1)'
  );
  const mutedTextColor = pick('rgb(107 114 128)', 'rgb(156 163 175)'); // gray-500 / gray-400

  const latestStartMs = useRef(segment.start);
  const latestStopMs = useRef(segment.stop);

  const debouncedChange = useMemo(
    () => debounce(changeSegmentTime, 500),
    [changeSegmentTime]
  );

  if (latestStartMs.current !== segment.start)
    latestStartMs.current = segment.start;
  if (latestStopMs.current !== segment.stop)
    latestStopMs.current = segment.stop;

  const validateAndUpdateTime = (
    field: 'start' | 'stop',
    newMs: number
  ): boolean => {
    const index = filteredItems.findIndex(s => s.id === segment.id);
    if (index === -1) {
      toast.error(t('could-not-locate-segment-index'));
      return false;
    }
    const prev = filteredItems[index - 1];
    const next = filteredItems[index + 1];

    if (field === 'start') {
      if (newMs >= latestStopMs.current) {
        toast.error(t('start-time-must-be-smaller-than-stop'));
        return false;
      }
      if (prev && newMs < prev.stop) {
        toast.error(t('start-time-must-be-after-previous-stop-time'));
        return false;
      }
      latestStartMs.current = newMs;
      debouncedChange(segment.id, 'start', 'ms', String(newMs));
      return true;
    } else {
      if (newMs <= latestStartMs.current) {
        toast.error(t('stop-time-must-be-greater-than-start-time'));
        return false;
      }
      if (next && newMs > next.start) {
        toast.error(t('stop-time-must-be-before-next-start-time'));
        return false;
      }
      latestStopMs.current = newMs;
      debouncedChange(segment.id, 'stop', 'ms', String(newMs));
      return true;
    }
  };

  const handleStartChange = (newMs: number) =>
    validateAndUpdateTime('start', newMs);
  const handleStopChange = (newMs: number) =>
    validateAndUpdateTime('stop', newMs);

  return (
    <div
      className="flex items-stretch text-[11px] leading-[14px] min-h-full border-r w-full"
      style={{ borderColor }}
    >
      {/* 4 fixed rows; tight gaps to fit 80–105px */}
      <div className="w-full grid grid-rows-[18px_22px_22px_22px] gap-y-0.5 overflow-hidden">
        {/* Row 1: Counters (single-line, truncates nicely) */}
        {counters && (
          <div
            className="h-[22px] flex items-center justify-between border-b text-[10px] leading-none px-2"
            style={{
              borderColor,
              backgroundColor: counterBgColor,
              color: counterTextColor
            }}
          >
            {/* Block total + status dot */}
            <div className="flex items-center gap-1.5 shrink-0">
              <span
                className={cn(
                  'tabular-nums',
                  counters.blockChars > counters.blockLimit &&
                    'text-red-600 font-medium'
                )}
              >
                {counters.blockChars}/{counters.blockLimit}
              </span>
              <span
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  counters.lineLengths.some(l => l > counters.lineLimit) ||
                    counters.blockChars > counters.blockLimit
                    ? 'bg-red-500'
                    : 'bg-emerald-400'
                )}
              />
            </div>
            {/* Per-line (comma list) with limit */}
            <div className="min-w-0 truncate">
              {counters.lineLengths.map((len, i) => (
                <span
                  key={i}
                  className={cn(
                    'tabular-nums',
                    len > counters.lineLimit && 'text-red-600 font-medium'
                  )}
                >
                  {len}
                  {i < counters.lineLengths.length - 1 ? ',' : ''}
                </span>
              ))}
              <span className="opacity-70">/ {counters.lineLimit}</span>
            </div>
          </div>
        )}
        {/* Row 2: ID + CPS */}
        <div
          className="flex justify-between items-center pl-1.5 pr-2 mt-1.5"
          style={{ color: mutedTextColor }}
        >
          <span
            className="inline-flex items-center rounded-full px-1 py-[2px] text-[10px] font-medium ring-1 ring-inset"
            style={{
              backgroundColor: idBgColor,
              color: idTextColor,
              boxShadow: `inset 0 0 0 1px ${idRingColor}`
            }}
          >
            #{segment.id}
          </span>
          <span className="flex items-center gap-1">
            <span className="text-[10px] uppercase tracking-wider font-medium">
              CPS
            </span>
            <span className="text-xs font-mono tabular-nums">
              {calculateCPS(segment.text, segment.start, segment.stop)}
            </span>
          </span>
        </div>

        {/* Row 3: Start */}
        <div className="flex items-center justify-between gap-1.5 h-[22px] px-2">
          <ArrowRight width={12} height={12} className="shrink-0" />
          <TimestampInput
            value={segment.start}
            onChange={handleStartChange}
            fieldType="start"
            /* ensure your TimestampInput renders compact (height ~22px) */
          />
        </div>

        {/* Row 4: Stop */}
        <div className="flex items-center justify-between gap-1.5 h-[22px] px-2">
          <ArrowLeft width={12} height={12} className="shrink-0" />
          <TimestampInput
            value={segment.stop}
            onChange={handleStopChange}
            fieldType="stop"
          />
        </div>
      </div>
    </div>
  );
};
